import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DotPattern } from '../../components/ui/dot-pattern/dot-pattern.js';

interface Segment {
  text: string;
  className?: string;
}

interface Line {
  segments: Segment[];
}

const TYPE_INTERVAL_MS = 38;

function prefersReducedMotion(): boolean {
  if (typeof matchMedia === 'undefined') return false;
  return matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Brand principle block — a pull-quote framed by a dotted backdrop and four
 * corner square markers. The blockquote is typed out character-by-character
 * on first viewport entry, with the same blinking caret as the header logo
 * trailing the cursor and remaining at the end once typing completes.
 *
 * Rendered as a `<div>` (not `<section>`) so the landing page's
 * seven-landmark contract is preserved.
 */
export function PrincipleQuote() {
  const { t } = useTranslation('landing');

  const lines: Line[] = useMemo(
    () => [
      { segments: [{ text: t('principle.line1'), className: 'font-semibold' }] },
      {
        segments: [
          { text: t('principle.line2a'), className: 'font-thin text-secondary' },
          { text: ' ' },
          { text: t('principle.line2b'), className: 'font-semibold' },
          { text: '.', className: 'text-accent-lime' },
        ],
      },
      {
        segments: [
          { text: t('principle.line3a'), className: 'font-thin text-secondary' },
          { text: ' ' },
          { text: t('principle.line3b'), className: 'font-semibold' },
          { text: ' ' },
          { text: t('principle.line3c'), className: 'font-thin text-secondary' },
          { text: '.', className: 'text-accent-lime' },
        ],
      },
    ],
    [t],
  );

  const fullText = useMemo(
    () => lines.map((l) => l.segments.map((s) => s.text).join('')).join(' '),
    [lines],
  );

  const totalChars = useMemo(
    () => lines.reduce((n, l) => n + l.segments.reduce((m, s) => m + s.text.length, 0), 0),
    [lines],
  );

  const wrapRef = useRef<HTMLDivElement>(null);
  const [started, setStarted] = useState(false);
  const [typed, setTyped] = useState(0);

  // Reduced motion: bypass the typewriter entirely.
  useEffect(() => {
    if (prefersReducedMotion()) {
      setStarted(true);
      setTyped(totalChars);
    }
  }, [totalChars]);

  // Start typing the first time the card scrolls into view.
  useEffect(() => {
    if (started) return;
    const node = wrapRef.current;
    if (!node) return;
    if (typeof IntersectionObserver === 'undefined') {
      setStarted(true);
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          setStarted(true);
          obs.disconnect();
        }
      },
      { threshold: 0.25 },
    );
    obs.observe(node);
    return () => {
      obs.disconnect();
    };
  }, [started]);

  // Advance one character per interval until the full text is typed.
  useEffect(() => {
    if (!started) return;
    if (typed >= totalChars) return;
    const id = setTimeout(() => setTyped((n) => n + 1), TYPE_INTERVAL_MS);
    return () => {
      clearTimeout(id);
    };
  }, [started, typed, totalChars]);

  // Compute visible character count for each line based on the current
  // global `typed` value.
  let remaining = typed;
  const lineStates = lines.map((line) => {
    const len = line.segments.reduce((n, s) => n + s.text.length, 0);
    const visible = Math.min(remaining, len);
    remaining -= visible;
    return { line, visible, len };
  });

  // Caret rides whichever line is currently receiving characters. Once
  // typing finishes, it stays on the last line.
  let caretLineIdx = 0;
  for (let i = lineStates.length - 1; i >= 0; i--) {
    if ((lineStates[i]?.visible ?? 0) > 0) {
      caretLineIdx = i;
      break;
    }
  }

  return (
    <div className="mx-auto w-full max-w-[1280px] px-6 py-20" ref={wrapRef}>
      <div className="relative flex flex-col items-center border border-accent-cyan/50">
        <DotPattern width={12} height={12} cr={0.8} className="fill-accent-cyan/10" />
        {/* Corner square markers */}
        <div aria-hidden="true" className="absolute -left-1.5 -top-1.5 h-3 w-3 bg-accent-cyan" />
        <div aria-hidden="true" className="absolute -bottom-1.5 -left-1.5 h-3 w-3 bg-accent-cyan" />
        <div aria-hidden="true" className="absolute -right-1.5 -top-1.5 h-3 w-3 bg-accent-cyan" />
        <div
          aria-hidden="true"
          className="absolute -bottom-1.5 -right-1.5 h-3 w-3 bg-accent-cyan"
        />

        <figure className="relative z-10 mx-auto w-full max-w-5xl px-6 py-12 md:px-10 md:py-20 xl:py-24">
          <figcaption className="mb-6 font-mono text-caption uppercase tracking-[0.25em] text-accent-cyan">
            {t('principle.eyebrow')}
          </figcaption>
          {/* aria-label exposes the full quote to assistive tech immediately
              even while the visible text is still being typed. */}
          <blockquote
            aria-label={fullText}
            className="text-[clamp(1.75rem,4.5vw,3.5rem)] font-semibold leading-[1.1] tracking-[-0.025em] text-primary"
          >
            {lineStates.map((ls, lineIdx) => {
              let budget = ls.visible;
              const renderedSegments = ls.line.segments.map((seg, segIdx) => {
                if (budget <= 0) return null;
                const take = Math.min(seg.text.length, budget);
                const slice = seg.text.slice(0, take);
                budget -= take;
                return (
                  <span key={segIdx} className={seg.className}>
                    {slice}
                  </span>
                );
              });
              return (
                <span key={lineIdx} className="block" aria-hidden="true">
                  {renderedSegments}
                  {started && lineIdx === caretLineIdx && <span className="agb-quote-caret" />}
                </span>
              );
            })}
          </blockquote>
        </figure>
      </div>
    </div>
  );
}
